import React, { useContext, useMemo, useCallback } from 'react';
import ParcelInfo from 'components/generics/Map/ParcelInfo';
import { ExploitationContext } from 'utils/context';
import { emptyCulture } from 'utils/culturesColors';
import {
  filterParcelFromActivityDate,
  getCultureCampaignKeyFromYear,
} from 'utils/Datagrid/CustomParcelstDataGridUtils';
import 'components/generics/Map/Map.style.scss';
import MapInteractionHook from 'components/generics/Map/MapInteraction.hook';

const CONSTRAINT_COLORS = {
  interdiction: { name: 'constraint-red', color: 'dc2626' },
  obligation: { name: 'constraint-green', color: '16a34a' },
  division: { name: 'constraint-yellow', color: 'f2f54e' },
  permission: { name: 'constraint-blue', color: '2563eb' },
  conversion: { name: 'constraint-purple', color: '560591' },
};

const DEFAULT_SELECTED = { name: 'constraint-selected', color: '38761D' };
const DEFAULT_UNSELECTED = { name: 'constraint-unselected', color: 'ffa07a' };

function MapParamsSimulation({
  updateParcel,
  forecastCampaign,
  name = 'map',
  fromProcedure = false,
  parcelListData,
  selectedParcels,
  highlightedParcels,
  constraints,
  cultures,
  showIrrigable = false,
}) {
  const { exploitationContext } = useContext(ExploitationContext);

  const parcelsData = useMemo(() => {
    if (fromProcedure) {
      return parcelListData || [];
    }
    return exploitationContext?.parcels
      ? filterParcelFromActivityDate(exploitationContext?.parcels, forecastCampaign)
      : [];
  }, [fromProcedure, parcelListData, exploitationContext?.parcels, forecastCampaign]);

  const getParentId = useCallback(
    (parcelId) => {
      const parcel = parcelsData.find((p) => p.id === parcelId);
      return parcel?.parent || null;
    },
    [parcelsData],
  );

  const isParcelSelected = useCallback(
    (parcelId) => {
      if (selectedParcels?.includes(parcelId)) return true;
      const parentId = getParentId(parcelId);
      if (parentId && selectedParcels?.includes(parentId)) return true;
      return false;
    },
    [selectedParcels, getParentId],
  );

  const constraintColorsByParcel = useMemo(() => {
    const map = {};
    if (!constraints?.length) return map;

    constraints.forEach((constraint) => {
      const colorEntry = CONSTRAINT_COLORS[constraint.constraint];
      if (!colorEntry) return;

      constraint.parcels?.forEach((parcelId) => {
        if (!map[parcelId]) {
          map[parcelId] = new Map();
        }
        map[parcelId].set(constraint.constraint, colorEntry);
      });
    });

    return map;
  }, [constraints]);

  const getCultureForParcel = (parcel, currentYear) => {
    const key = getCultureCampaignKeyFromYear(currentYear);
    return parcel?.parcelCultures?.[key] || { ...emptyCulture, year: currentYear };
  };

  const getPolygonStyleParams = (parcel, culture, defaultOpacity) => {
    const isSelected = isParcelSelected(parcel.id);
    const parcelConstraintColors = constraintColorsByParcel[parcel.id];

    let finalOpacity = defaultOpacity;
    if (highlightedParcels) {
      finalOpacity = highlightedParcels.includes(parcel.id) ? 1 : 0.3;
    }

    let color;
    let hasConstraints = false;

    if (parcelConstraintColors && parcelConstraintColors.size > 0) {
      const uniqueColors = [...parcelConstraintColors.values()];
      if (uniqueColors.length === 1) {
        color = uniqueColors[0];
      } else {
        color = uniqueColors;
        hasConstraints = true;
      }
    } else {
      color = isSelected ? DEFAULT_SELECTED : DEFAULT_UNSELECTED;
    }

    const constraintCulturesColors = [
      DEFAULT_SELECTED,
      DEFAULT_UNSELECTED,
      ...Object.values(CONSTRAINT_COLORS),
    ];
    const parentParcel = parcel.parent ? parcelsData.find((p) => p.id === parcel.parent) : parcel;
    const isIrrigable = showIrrigable && !!parentParcel?.isIrrigable;
    return {
      color,
      opacity: finalOpacity,
      organicState: parcel?.organicState,
      culturesColors: constraintCulturesColors,
      conversionDate: parcel?.conversionDate,
      year: culture?.year,
      hasConstraints,
      isIrrigable,
    };
  };

  const handleParcelClick = (parcel) => {
    if (!parcel) return;

    const parentId = parcel.parent || parcel.id;
    updateParcel(parentId);
  };

  const {
    parcelInfo,
    modalCoords,
    mapElement,
    popup,
    popupContentRef,
    clearPopup,
    getPopupStyle,
    getPopupInnerStyle,
  } = MapInteractionHook({
    mapId: name,
    parcelsData,
    initialYear: forecastCampaign?.startYear,
    getCultureForParcel,
    getPolygonStyleParams,
    onParcelClick: handleParcelClick,
    dependencies: [selectedParcels, highlightedParcels, constraintColorsByParcel],
  });

  return (
    <div>
      <div className="tab_container">
        <div ref={mapElement} className="map_container">
          <div id={name} className="map_element" onMouseOut={clearPopup}></div>

          {parcelInfo && modalCoords && (
            <div ref={popup} style={getPopupStyle()}>
              <div ref={popupContentRef} className="ol-popup" style={getPopupInnerStyle()}>
                <ParcelInfo
                  parcelInfo={parcelInfo}
                  from="mapParcels"
                  forecastCampaign={forecastCampaign}
                  constraints={constraints}
                  cultures={cultures}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MapParamsSimulation;
